/**
 * Vite SPA entry. Mounts React, the TanStack Router, and the
 * persisted TanStack Query client.
 *
 * Migration note: this is the equivalent of Next.js's
 * frontend/src/app/layout.tsx providers stack, but for a pure SPA —
 * no server, no hydration, no async boundaries.
 */

import "@/styles/globals.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import {
  PersistQueryClientProvider,
} from "@tanstack/react-query-persist-client";
import { Toaster, toast } from "sonner";
import { registerSW } from "virtual:pwa-register";

import { queryClient, persister } from "@/lib/query-client";
import { shouldPersistQuery } from "@/lib/payroll-setup-queries";
import { routeTree } from "@/routeTree.gen";
import { AuthProvider } from "@/lib/auth";
import { RouteError } from "@/components/shared/route-error";
import { initSentry } from "@/lib/sentry";

// Error tracking — must run before the router mounts so render-time
// throws during the first paint are captured. No-op without a DSN.
initSentry();

// Dark-mode preview for development only (docs/72 step 0.9). The app is
// forced light until dark mode ships; `?scheme=dark` adds the `.dark` class
// so the dark tokens can be checked locally. Production never sets it.
if (import.meta.env.DEV && new URLSearchParams(location.search).get("scheme") === "dark") {
  document.documentElement.classList.add("dark");
}

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // Preserve route loaders' freshness; matches TanStack Query's stale
  // gating so an explicit nav re-uses cached data without refetch.
  defaultPreloadStaleTime: 0,
  // Global error fallback (2026-06-04). Covers all 140 authenticated
  // routes + auth flow + marketing in one line — without it, a
  // render-time throw lands on TanStack's built-in DefaultErrorComponent
  // which displays the raw stack trace. Per-route overrides can pass
  // their own `errorComponent` for module-specific copy.
  defaultErrorComponent: RouteError,
  context: {
    // QueryClient is exposed to route loaders so they can prefetch on
    // hover without re-wiring per-route.
    queryClient,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("missing #root — index.html is malformed");

createRoot(rootEl).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: persister!,
        // Discard persisted queries older than 24h to avoid serving
        // stale per-user data after a long absence.
        maxAge: 24 * 60 * 60 * 1000,
        // Some reads must never come back from localStorage after a
        // reload (the payroll checklist) — see lib/payroll-setup-queries.
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
      }}
      // Restored data is a snapshot taken up to `throttleTime` before the
      // last unload (see lib/query-client.ts): paint it, then mark every
      // query stale so active ones refetch at once. Without this a reload
      // within that window showed the pre-mutation list for up to 5 min
      // ("Employee not found" right after adding someone, a requisition
      // "isn't available" right after creating it).
      onSuccess={() => {
        void queryClient.invalidateQueries();
      }}
    >
      {/* AuthProvider runs the mount-time /me probe and exposes user /
          tenant / features / login / logout to every route via
          useAuth(). Sits inside PersistQueryClientProvider because
          authApi calls and downstream module queries all share the same
          QueryClient. */}
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
      {/* Toasts start below the 64px top bar, never over it: on desktop it
          holds Inbox, the bell and the account menu (a toast there blocked
          the Inbox button). Phones (≤600px, sonner's own breakpoint) also
          clear the notch and stay clear of the bottom bar and sticky action
          bars. The close button gets a 44px tap area on touch (docs/71 §63). */}
      <Toaster
        richColors
        closeButton
        position="top-right"
        offset={{ top: "calc(4rem + 0.5rem)", right: 16 }}
        mobileOffset={{
          top: "calc(4rem + 0.5rem + env(safe-area-inset-top))",
          left: 16,
          right: 16,
        }}
        toastOptions={{
          classNames: {
            closeButton: "pointer-coarse:after:absolute pointer-coarse:after:-inset-3",
          },
        }}
      />
    </PersistQueryClientProvider>
  </StrictMode>,
);

// PWA update flow: when a new version is deployed and the SW is waiting,
// show a persistent toast with a "Reload" action rather than
// auto-reloading the page out from under the user (which could interrupt
// an attendance punch or a payroll lock). updateSW(true) activates the
// new worker and reloads. Fixes the "returning users see a stale UI" gap.
const updateSW = registerSW({
  onNeedRefresh() {
    toast("A new version is available.", {
      description: "Reload to get the latest update.",
      duration: Infinity,
      action: { label: "Reload", onClick: () => void updateSW(true) },
    });
  },
});
